import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Admin.module.css';

/**
 * KUCUK SVG ZAMAN SERISI — kutuphane YOK.
 *
 * Neden bir grafik bagimliligi eklenmedi: bu ekranda cizilen sey tek seri, tek
 * eksen ve etkilesimsiz. Bir grafik kutuphanesi paket boyutunu ve tema
 * yuzeyini (kendi renkleri, kendi yazi tipleri) beraberinde getirir; renkler
 * zaten `--color-chart-series-*` jetonlarinda tanimli ve acik/koyu temada
 * kendiliginden dogru degeri aliyor. `currentColor` uzerinden okunuyorlar:
 * SVG icinde `var()` bazi tarayicilarda `stroke` icin guvenilmez.
 *
 * ERISILEBILIRLIK: SVG'nin kendisi `aria-hidden`. Gercek icerik metin ozeti +
 * acilir bir TABLO — ekran okuyucu icin "yukselen bir cizgi" hicbir sey ifade
 * etmez, sayilar eder. Tablo goren kullanici icin de yararli, bu yuzden
 * gizlenmis degil katlanmis durumda.
 */

export type SeriesPoint = {
  /** Eksen etiketi — CEVRILMIS/BICIMLENMIS gelir. */
  label: string;
  value: number;
};

export type SeriesChartProps = {
  points: readonly SeriesPoint[];
  /** Grafigin adi (cevrilmis). */
  title: string;
  /** Degerin okunabilir hali — bicimlendirme cagri yerinin isi. */
  formatValue: (value: number) => string;
  /** 1..5 arasi jeton indeksi. */
  seriesIndex?: 1 | 2 | 3 | 4 | 5;
  /** Nokta yoksa gosterilecek metin (cevrilmis). */
  emptyLabel: string;
};

const VIEW_WIDTH = 640;
const VIEW_HEIGHT = 180;
const PAD_X = 8;
const PAD_Y = 12;

export function SeriesChart({
  points,
  title,
  formatValue,
  seriesIndex = 1,
  emptyLabel,
}: SeriesChartProps) {
  const { t } = useTranslation();
  const tableId = useId();

  if (points.length === 0) {
    return <p className={styles.muted}>{emptyLabel}</p>;
  }

  const values = points.map((point) => point.value);
  // Sifir bolme korumasi: tum degerler 0 ise cizgi tabana oturur.
  const max = Math.max(...values, 0);
  const span = max === 0 ? 1 : max;
  const usableWidth = VIEW_WIDTH - PAD_X * 2;
  const usableHeight = VIEW_HEIGHT - PAD_Y * 2;

  const x = (index: number): number =>
    points.length === 1
      ? VIEW_WIDTH / 2
      : PAD_X + (usableWidth * index) / (points.length - 1);
  const y = (value: number): number => PAD_Y + usableHeight - (usableHeight * value) / span;

  const line = points.map((point, index) => `${x(index)},${y(point.value)}`).join(' ');
  const area = `${PAD_X},${PAD_Y + usableHeight} ${line} ${x(points.length - 1)},${
    PAD_Y + usableHeight
  }`;

  const total = values.reduce((sum, value) => sum + value, 0);
  const peakIndex = values.indexOf(max);
  const peak = points[peakIndex];

  return (
    <figure className={styles.chartFigure}>
      <svg
        className={[styles.chart, styles[`series${String(seriesIndex)}`]]
          .filter(Boolean)
          .join(' ')}
        viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
        preserveAspectRatio="none"
        /* Icerik asagidaki metin ozeti ve tabloda; cizim yalniz gorsel. */
        aria-hidden="true"
        focusable="false"
      >
        {/* Izgara: taban ve iki ara cizgi. Eksen etiketi YOK — bu boyutta
            okunmuyor; sayilar tabloda. */}
        {[0, 0.5, 1].map((ratio) => (
          <line
            key={ratio}
            className={styles.chartGrid}
            x1={PAD_X}
            x2={VIEW_WIDTH - PAD_X}
            y1={PAD_Y + usableHeight * ratio}
            y2={PAD_Y + usableHeight * ratio}
          />
        ))}
        <polygon className={styles.chartArea} points={area} />
        <polyline className={styles.chartLine} points={line} />
      </svg>

      <figcaption className={styles.chartCaption}>
        <span className={styles.muted}>
          {title} — {t('admin.metrics.chartSummary', {
            total: formatValue(total),
            peak: formatValue(max),
            peakLabel: peak?.label ?? '',
          })}
        </span>
        <details>
          <summary className={styles.chartTableToggle}>{t('admin.metrics.showTable')}</summary>
          <table className={styles.chartTable} id={tableId}>
            <caption className={styles.visuallyHidden}>{title}</caption>
            <thead>
              <tr>
                <th scope="col">{t('admin.metrics.bucket')}</th>
                <th scope="col">{t('admin.metrics.value')}</th>
              </tr>
            </thead>
            <tbody>
              {points.map((point) => (
                <tr key={point.label}>
                  <th scope="row">{point.label}</th>
                  <td>{formatValue(point.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      </figcaption>
    </figure>
  );
}
